/**
 * Authentication store — Keycloak OIDC via PKCE flow.
 * The employee portal authenticates against the "employee" Keycloak realm.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL as string
const REALM = import.meta.env.VITE_KEYCLOAK_REALM ?? 'employee'
const CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'employee-portal'
const REDIRECT_URI = window.location.origin + '/login'

interface TokenSet {
  accessToken: string
  refreshToken: string
  expiresAt: number // Unix timestamp ms
}

export const useAuthStore = defineStore('auth', () => {
  const tokenSet = ref<TokenSet | null>(_loadFromStorage())
  const userInfo = ref<Record<string, unknown> | null>(null)

  const isAuthenticated = computed(() => {
    if (!tokenSet.value) return false
    return tokenSet.value.expiresAt > Date.now()
  })

  const accessToken = computed(() => tokenSet.value?.accessToken ?? null)

  async function initSession() {
    if (!tokenSet.value) return
    if (tokenSet.value.expiresAt < Date.now() + 60_000) {
      await refreshToken()
    }
    if (tokenSet.value && !userInfo.value) {
      await fetchUserInfo()
    }
  }

  async function getAccessToken(): Promise<string | null> {
    if (!tokenSet.value) return null
    if (tokenSet.value.expiresAt < Date.now() + 60_000) {
      await refreshToken()
    }
    return tokenSet.value?.accessToken ?? null
  }

  async function refreshToken() {
    if (!tokenSet.value?.refreshToken) {
      logout()
      return
    }
    try {
      const params = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: CLIENT_ID,
        refresh_token: tokenSet.value.refreshToken,
      })
      const res = await fetch(
        `${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/token`,
        { method: 'POST', body: params },
      )
      if (!res.ok) throw new Error('refresh failed')
      const data = await res.json()
      _setTokens(data)
    } catch {
      logout()
    }
  }

  async function fetchUserInfo() {
    const token = await getAccessToken()
    if (!token) return
    const res = await fetch(
      `${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/userinfo`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
    if (res.ok) {
      userInfo.value = await res.json()
    }
  }

  function redirectToLogin() {
    const state = crypto.randomUUID()
    sessionStorage.setItem('oauth_state', state)
    const url = new URL(`${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/auth`)
    url.searchParams.set('response_type', 'code')
    url.searchParams.set('client_id', CLIENT_ID)
    url.searchParams.set('redirect_uri', REDIRECT_URI)
    url.searchParams.set('scope', 'openid profile email')
    url.searchParams.set('state', state)
    window.location.href = url.toString()
  }

  async function handleCallback(code: string, state: string) {
    const savedState = sessionStorage.getItem('oauth_state')
    if (state !== savedState) throw new Error('Invalid OAuth state')
    sessionStorage.removeItem('oauth_state')

    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      code,
    })
    const res = await fetch(
      `${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/token`,
      { method: 'POST', body: params },
    )
    if (!res.ok) throw new Error('Token exchange failed')
    const data = await res.json()
    _setTokens(data)
    await fetchUserInfo()
  }

  function _setTokens(data: { access_token: string; refresh_token: string; expires_in: number }) {
    const ts: TokenSet = {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresAt: Date.now() + data.expires_in * 1000,
    }
    tokenSet.value = ts
    localStorage.setItem('cf_employee_tokens', JSON.stringify(ts))
  }

  function logout() {
    tokenSet.value = null
    userInfo.value = null
    localStorage.removeItem('cf_employee_tokens')
    // Redirect to Keycloak logout
    const url = new URL(`${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/logout`)
    url.searchParams.set('client_id', CLIENT_ID)
    url.searchParams.set('post_logout_redirect_uri', window.location.origin + '/login')
    window.location.href = url.toString()
  }

  return {
    isAuthenticated,
    accessToken,
    userInfo,
    initSession,
    getAccessToken,
    redirectToLogin,
    handleCallback,
    logout,
  }
})

function _loadFromStorage(): TokenSet | null {
  try {
    const raw = localStorage.getItem('cf_employee_tokens')
    if (!raw) return null
    return JSON.parse(raw) as TokenSet
  } catch {
    return null
  }
}
